<script setup>
import { ref, watch, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import ContactWorkflowsAPI from 'dashboard/api/contactWorkflows';

// Bloco "Fluxos" na tela do contato, com a mesma cara do bloco de segmentos.
//
// Tirar do fluxo mora aqui TAMBÉM — e para o caso mais comum este é o melhor
// lugar: "essa pessoa respondeu, tira ela de tudo" começa olhando a pessoa,
// não o fluxo.
const props = defineProps({
  contactId: { type: [String, Number], default: null },
});

const { t } = useI18n();
const execucoes = ref([]);
const carregando = ref(false);
const parando = ref(null);

const ativos = computed(() => execucoes.value.filter(e => e.active));

const carregar = async () => {
  if (!props.contactId) return;
  carregando.value = true;
  try {
    const { data } = await ContactWorkflowsAPI.forContact(props.contactId);
    execucoes.value = data?.payload || [];
  } catch {
    execucoes.value = [];
  } finally {
    carregando.value = false;
  }
};

const tirar = async execucao => {
  parando.value = execucao.id;
  try {
    await ContactWorkflowsAPI.stop(props.contactId, execucao.workflow_id);
    toast.success(t('CONTACT_WORKFLOWS.STOPPED'));
    await carregar();
  } catch {
    toast.error(t('CONTACT_WORKFLOWS.STOP_FAILED'));
  } finally {
    parando.value = null;
  }
};

const tirarDeTudo = async () => {
  parando.value = 'all';
  try {
    await ContactWorkflowsAPI.stop(props.contactId);
    toast.success(t('CONTACT_WORKFLOWS.STOPPED'));
    await carregar();
  } catch {
    toast.error(t('CONTACT_WORKFLOWS.STOP_FAILED'));
  } finally {
    parando.value = null;
  }
};

const situacao = execucao => {
  if (execucao.active) {
    return execucao.resumes_at
      ? t('CONTACT_WORKFLOWS.WAITING_UNTIL', {
          step: execucao.current_step || '—',
          date: new Date(execucao.resumes_at).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }),
        })
      : t('CONTACT_WORKFLOWS.RUNNING');
  }
  return execucao.exit_reason
    ? t(`CONTACT_WORKFLOWS.EXIT.${execucao.exit_reason.toUpperCase()}`, '')
    : t('CONTACT_WORKFLOWS.FINISHED');
};

onMounted(carregar);
watch(() => props.contactId, carregar);
</script>

<template>
  <div v-if="execucoes.length" class="flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <h4
        class="text-[10px] font-bold uppercase tracking-[0.09em] text-n-slate-10"
      >
        {{ t('CONTACT_WORKFLOWS.TITLE') }}
      </h4>
      <button
        v-if="ativos.length > 1"
        class="ml-auto text-[11px] font-semibold text-n-ruby-11 hover:underline disabled:opacity-50"
        :disabled="parando === 'all'"
        @click="tirarDeTudo"
      >
        {{ t('CONTACT_WORKFLOWS.STOP_ALL') }}
      </button>
    </div>

    <div
      v-for="execucao in execucoes"
      :key="execucao.id"
      class="flex items-start gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
    >
      <span
        class="mt-1.5 size-1.5 shrink-0 rounded-full"
        :class="execucao.active ? 'bg-n-teal-10' : 'bg-n-slate-8'"
      />
      <span class="min-w-0 flex-1">
        <b class="block truncate text-[12.5px] text-n-slate-12">
          {{ execucao.workflow_name }}
        </b>
        <span class="text-[11px] text-n-slate-10">{{
          situacao(execucao)
        }}</span>
      </span>
      <button
        v-if="execucao.active"
        class="shrink-0 rounded-md border border-n-ruby-6 px-1.5 py-0.5 text-[10.5px] font-semibold text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
        :disabled="parando === execucao.id"
        @click="tirar(execucao)"
      >
        {{ t('CONTACT_WORKFLOWS.STOP') }}
      </button>
    </div>
  </div>
</template>
